/**
 * S2 \u6389\u843d\u4e0e\u8fde\u9501\u7cfb\u7edf \u2014\u2014 \u5b9e\u73b0\u81ea design/gdd/systems/S2-drop-cascade.md\u3002
 *
 * \u804c\u8d23\uff1a\u91cd\u529b\u538b\u5b9e\u3001\u5206\u6bb5\u8865\u6599\u3001\u8fde\u9501\u8fed\u4ee3\u3001\u6b7b\u5c40\u91cd\u6392\u3002
 * \u94c1\u5f8b\uff1a\u6574\u4e2a settle() \u671f\u95f4\u8f93\u5165\u9501\u5b9a\uff1b\u53ea\u6709 BoardStable \u624d\u89e3\u9501\uff08S7 \u4e0d\u5f97\u963b\u585e\uff09\u3002
 */

import { BOARD_SIZE, ALL_COLORS, type Board0, type Coord, type Cell, type PieceColor, type SpecialType } from './types';
import { getCell, findMatchGroups, hasAnyLegalSwap, createEmptyBoard } from './board';
import type { EventBus } from './bus';

/** \u5b89\u5168\u8fed\u4ee3\u4e0a\u9650\uff08S2 \u5e38\u91cf\uff09 */
export const SAFETY_ITERATION_CAP = 64;
/** \u91cd\u6392\u6709\u754c\u91cd\u8bd5\u6b21\u6570 */
const RESHUFFLE_RETRY_CAP = 32;

export interface CascadeStep {
  level: number;
  changedCells: Coord[];
}

/**
 * \u4e00\u6b21\u91cd\u529b\u4f4d\u79fb\u8bb0\u5f55\uff1a\u4ece (fromC,fromR) \u843d\u5230 (toC,toR)\u3002
 * \u4f9b\u6e32\u67d3\u5c42\u505a\u4e0b\u843d\u8865\u95f4\uff08from \u5728\u76d8\u5916\u8868\u793a\u65b0\u751f\u6210\uff09\u3002
 */
export interface FallRecord {
  fromC: number;
  fromR: number;
  toC: number;
  toR: number;
  /** \u662f\u5426\u4e3a\u65b0\u751f\u6210\u7684\u68cb\u5b50\uff08\u4ece\u76d8\u5916\u843d\u5165\uff09 */
  spawned: boolean;
  /** \u68cb\u5b50\u989c\u8272\uff08\u4f9b\u6e32\u67d3\u5c42\u4e0e\u89c6\u89c9\u68cb\u76d8\u56de\u653e\u7528\uff09 */
  color?: PieceColor;
  /** \u7279\u6b8a\u5757\u7c7b\u578b\uff08\u82e5\u4e0b\u843d\u7684\u662f\u7279\u6b8a\u5757\uff09 */
  special?: SpecialType;
}

/** \u4e00\u4e2a\u8fde\u9501\u7ea7\u7684\u5b8c\u6574\u52a8\u4f5c\u8bb0\u5f55 */
export interface CascadeLevelRecord {
  level: number;
  /** \u672c\u7ea7\u88ab\u6d88\u9664\u7684\u683c\uff08\u542b\u989c\u8272\uff0c\u4f9b\u788e\u88c2\u7279\u6548\u7528\uff09 */
  cleared: { cell: Coord; color: PieceColor; special?: SpecialType }[];
  /** \u672c\u7ea7\u7684\u4e0b\u843d/\u8865\u6599 */
  falls: FallRecord[];
}

export interface SettleResult {
  /** \u6700\u7ec8\u8fde\u9501\u7ea7\u6570\uff08\u9996\u6b21\u6d88\u9664 = 1\uff09 */
  finalCascadeLevel: number;
  /** \u5404\u7ea7\u53d8\u5316\u683c */
  steps: CascadeStep[];
  /** \u6bcf\u4e00\u7ea7\u7684\u6d88\u9664\u7ec4\uff08\u4f9b S6 \u8ba1\u5206\uff09 */
  groupsByLevel: { level: number; groups: ReturnType<typeof findMatchGroups> }[];
  /** \u6bcf\u4e00\u7ea7\u7684\u5b8c\u6574\u52a8\u4f5c\u8bb0\u5f55\uff08\u4f9b\u6e32\u67d3\u5c42\u64ad\u653e\uff09 */
  records: CascadeLevelRecord[];
  /** \u662f\u5426\u6267\u884c\u4e86\u91cd\u6392 */
  reshuffled: boolean;
  /** \u91cd\u6392\u7b56\u7565\uff08\u82e5\u6709\uff09 */
  reshufflePolicy?: 'PRESERVE_COUNTS' | 'FULL_RANDOM';
}

/**
 * \u4e3b\u6d41\u7a0b\uff1a\u4ece\u5f53\u524d\u76d8\u9762\u51fa\u53d1\uff0c\u53cd\u590d\u300c\u5339\u914d \u2192 \u6d88\u9664 \u2192 \u91cd\u529b \u2192 \u8865\u6599\u300d
 * \u76f4\u5230\u65e0\u5339\u914d\uff0c\u518d\u68c0\u6b7b\u5c40\u3002
 *
 * opts.forceGravityFirst\uff1a\u9996\u8c03\u65f6\u82e5\u8c03\u7528\u65b9\u5df2\u81ea\u884c\u6d88\u9664\u4e86\u7b2c\u4e00\u6ce2\uff08\u5982 trySwap\uff09\uff0c
 * \u5219\u7b2c\u4e00\u8fed\u4ee3\u8df3\u8fc7\u5339\u914d\u68c0\u6d4b\uff0c\u76f4\u63a5\u6267\u884c\u91cd\u529b+\u8865\u6599\u2014\u2014\u5426\u5219\u6e05\u7a7a\u540e\u627e\u4e0d\u5230\u5339\u914d\u4f1a\u76f4\u63a5\u9000\u51fa\uff0c
 * \u5bfc\u81f4\u68cb\u5b50\u6c38\u8fdc\u4e0d\u4e0b\u5760\u3001\u9876\u90e8\u6c38\u8fdc\u4e0d\u8865\u6599\uff08\u5386\u53f2 bug\uff09\u3002
 */
export function settle(
  board: Board0,
  bus: EventBus,
  rand: () => number = Math.random,
  opts?: { forceGravityFirst?: boolean },
  /** \u4e0a\u4e00\u6b65\u4e3b\u52a8\u6d88\u9664\u5f15\u53d1\u7684\u7ec4\uff08\u7b2c 1 \u7ea7\uff09 */
  initialGroups?: ReturnType<typeof findMatchGroups>,
): SettleResult {
  const steps: CascadeStep[] = [];
  const groupsByLevel: SettleResult['groupsByLevel'] = [];
  const records: CascadeLevelRecord[] = [];
  let level = 0;
  let reshuffled = false;
  let reshufflePolicy: SettleResult['reshufflePolicy'];
  let forceFirst = opts?.forceGravityFirst ?? false;

  bus.emit('CascadeStarted', {});

  let pending = initialGroups;

  while (true) {
    if (level >= SAFETY_ITERATION_CAP) {
      console.warn('[S2] \u8fbe\u5230\u5b89\u5168\u8fed\u4ee3\u4e0a\u9650\uff0c\u5f3a\u5236\u505c\u6b62');
      break;
    }

    let groups = pending ?? findMatchGroups(board);
    pending = undefined;

    // \u9996\u8fed\u4ee3\u5f3a\u5236\u91cd\u529b\u6a21\u5f0f\uff1a\u65e0\u89c6\u5339\u914d\uff0c\u76f4\u63a5\u843d\u5b9e\u5f53\u524d\u76d8\u9762
    if (forceFirst && groups.length === 0) {
      forceFirst = false;
      const falls = gravity(board);
      const spawned = refill(board, rand);
      if (falls.length > 0 || spawned.length > 0) {
        records.push({ level: 0, cleared: [], falls: [...falls, ...spawned] });
        steps.push({ level: 0, changedCells: falls.map((f) => ({ c: f.toC, r: f.toR })) });
      }
      continue;
    }

    if (groups.length === 0) break;

    level += 1;
    groupsByLevel.push({ level, groups });

    // \u6d88\u9664\uff08\u8bb0\u5f55\u989c\u8272\uff0c\u4f9b\u788e\u88c2\u7279\u6548\uff09
    const changed: Coord[] = [];
    const cleared: CascadeLevelRecord['cleared'] = [];
    for (const g of groups) {
      for (const p of g.cells) {
        const cell = getCell(board, p);
        if (cell && (cell.kind === 'PIECE' || cell.kind === 'SPECIAL')) {
          if (cell.color) {
            cleared.push({ cell: p, color: cell.color, special: cell.special });
          }
          board.cells[p.r][p.c] = { kind: 'EMPTY' };
          changed.push(p);
        }
      }
    }

    bus.emit('CascadeStep', { level, changedCells: changed });

    // \u91cd\u529b + \u8865\u6599\uff08\u5404\u81ea\u56de\u62a5\u4f4d\u79fb\u8bb0\u5f55\uff09
    const falls = gravity(board);
    const spawned = refill(board, rand);

    records.push({ level, cleared, falls: [...falls, ...spawned] });
    steps.push({ level, changedCells: [...changed, ...falls.map((f) => ({ c: f.toC, r: f.toR }))] });
  }

  // \u6b7b\u5c40\u68c0\u6d4b
  if (!hasAnyLegalSwap(board)) {
    bus.emit('DeadlockDetected', {});
    const policy = reshuffle(board, rand);
    reshuffled = true;
    reshufflePolicy = policy;
    bus.emit('ReshufflePerformed', { policy });
    // \u91cd\u6392\u540e\u518d\u8dd1\u4e00\u8f6e\u6d88\u9664
    const after = settle(board, bus, rand);
    return {
      finalCascadeLevel: level + after.finalCascadeLevel,
      steps: [...steps, ...after.steps],
      groupsByLevel: [...groupsByLevel, ...after.groupsByLevel],
      records: [...records, ...after.records],
      reshuffled: true,
      reshufflePolicy: policy,
    };
  }

  bus.emit('BoardStable', { finalCascadeLevel: level });
  return { finalCascadeLevel: level, steps, groupsByLevel, records, reshuffled, reshufflePolicy };
}

/**
 * S2 \u00a72.2\uff1a\u6309\u5217\u5206\u6bb5\u3001\u81ea\u5e95\u5411\u4e0a\u538b\u5b9e\uff0c\u4e0d\u7a7f\u969c\u788d\u3002
 * \u8fd4\u56de\u3010\u4f4d\u79fb\u8bb0\u5f55\u3011\uff0c\u4f9b\u6e32\u67d3\u5c42\u505a\u4e0b\u843d\u8865\u95f4\u3002
 */
export function gravity(board: Board0): FallRecord[] {
  const falls: FallRecord[] = [];
  for (let c = 0; c < BOARD_SIZE; c++) {
    let write = BOARD_SIZE - 1;
    for (let r = BOARD_SIZE - 1; r >= 0; r--) {
      const cell = board.cells[r][c];
      if (cell.kind === 'OBSTACLE') {
        write = r - 1;
        continue;
      }
      if (cell.kind === 'EMPTY') continue;
      if (write !== r) {
        board.cells[write][c] = cell;
        board.cells[r][c] = { kind: 'EMPTY' };
        falls.push({
          fromC: c, fromR: r,
          toC: c, toR: write,
          spawned: false,
          color: cell.color,
          special: cell.special,
        });
      }
      write -= 1;
    }
  }
  return falls;
}

/**
 * S2 \u00a72.3\uff1a\u5206\u6bb5\u4f9b\u6599\uff0c6 \u8272\u7b49\u6982\u7387\uff08refillWeights \u9501\u5b9a\uff0cE8\uff09\u3002
 * \u8fd4\u56de\u3010\u65b0\u751f\u6210\u4f4d\u79fb\u8bb0\u5f55\u3011\uff0cfrom \u5728\u76d8\u5916\uff08\u8d1f\u503c\uff09\u8868\u793a\u4ece\u4e0a\u65b9\u843d\u5165\u3002
 */
export function refill(board: Board0, rand: () => number = Math.random): FallRecord[] {
  const spawned: FallRecord[] = [];
  for (let c = 0; c < BOARD_SIZE; c++) {
    // \u7edf\u8ba1\u8be5\u5217\u7a7a\u4f4d\u4e2a\u6570\uff0c\u7528\u4e8e\u8ba1\u7b97\u76d8\u5916\u8d77\u70b9
    let emptyCount = 0;
    for (let r = 0; r < BOARD_SIZE; r++) if (board.cells[r][c].kind === 'EMPTY') emptyCount++;

    // \u8bb0\u5f55\u8be5\u5217\u6700\u4e0a\u65b9\u7684\u5df2\u6709\u975e\u7a7a\u7d22\u5f15\uff0c\u7528\u4e8e\u5806\u53e0\u8d77\u70b9
    let spawnSlot = 0;
    for (let r = 0; r < BOARD_SIZE; r++) {
      const cell = board.cells[r][c];
      if (cell.kind !== 'EMPTY') continue;
      const color = ALL_COLORS[Math.floor(rand() * ALL_COLORS.length)] ?? ALL_COLORS[0];
      board.cells[r][c] = { kind: 'PIECE', color };
      // \u76d8\u5916\u884c\u53f7\uff1a\u4ece -emptyCount \u5f00\u59cb\u4f9d\u6b21\u5411\u4e0a\u6392
      const fromR = -1 - spawnSlot;
      spawnSlot++;
      spawned.push({
        fromC: c, fromR,
        toC: c, toR: r,
        spawned: true,
        color,
      });
    }
  }
  return spawned;
}

/**
 * S2 \u00a72.4\uff1a\u6b7b\u5c40\u91cd\u6392\u3002
 * \u4f18\u5148 PRESERVE_COUNTS\uff08\u4fdd\u7559\u5404\u8272\u6570\u91cf\uff09\uff0c\u6709\u754c\u91cd\u8bd5\u540e\u9000\u5316 FULL_RANDOM\u3002
 */
export function reshuffle(
  board: Board0,
  rand: () => number = Math.random,
): 'PRESERVE_COUNTS' | 'FULL_RANDOM' {
  // \u7edf\u8ba1\u5404\u8272\u6570\u91cf
  const counts = new Map<PieceColor, number>();
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      const cell = board.cells[r][c];
      if (cell.color) counts.set(cell.color, (counts.get(cell.color) ?? 0) + 1);
    }
  }

  for (let attempt = 0; attempt < RESHUFFLE_RETRY_CAP; attempt++) {
    const pool: PieceColor[] = [];
    for (const [color, n] of counts) for (let i = 0; i < n; i++) pool.push(color);
    shuffle(pool, rand);

    // \u5c06 pool \u586b\u5165\u975e\u969c\u788d\u975e\u7a7a\u683c\u4f4d
    let idx = 0;
    const snapshot: Cell[][] = [];
    for (let r = 0; r < BOARD_SIZE; r++) {
      snapshot.push(board.cells[r].map((x) => ({ ...x })));
    }
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        const cell = board.cells[r][c];
        if (cell.kind === 'OBSTACLE') continue;
        board.cells[r][c] = { kind: 'PIECE', color: pool[idx++] ?? ALL_COLORS[0] };
      }
    }

    if (findMatchGroups(board).length === 0 && hasAnyLegalSwap(board)) {
      return 'PRESERVE_COUNTS';
    }
    // \u56de\u6eda\u91cd\u8bd5
    for (let r = 0; r < BOARD_SIZE; r++) board.cells[r] = snapshot[r];
  }

  // \u9000\u5316\uff1a\u5b8c\u5168\u968f\u673a\u91cd\u751f
  const fresh = createEmptyBoard();
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      const color = ALL_COLORS[Math.floor(rand() * ALL_COLORS.length)] ?? ALL_COLORS[0];
      fresh.cells[r][c] = { kind: 'PIECE', color };
    }
  }
  board.cells = fresh.cells;
  return 'FULL_RANDOM';
}

function shuffle<T>(arr: T[], rand: () => number): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}
